/* Biometric module: devices, punches, mappings, commands + device-side simulators. */
import { parseCsvPunches, parseZkAttlog, parseZkUsers, VENDORS } from "../../domain/biometric";
import { addDays, isISODate } from "../../domain/dates";
import type * as T from "../types";
import { E, isInt, randomHex, str, type Ctx } from "./ctx";
import { unmatched } from "./derived";
import { ingest, parseWebhook, tzOffsetMinutes, validTimezone } from "./ingest";
import type { DeviceRec } from "./state";

export const COMMAND_KINDS: T.DeviceCommand["kind"][] = ["sync_user", "delete_user", "reboot", "query_logs", "info", "set_time", "clear_logs"];
const DIRECTION_MODES = ["device", "first-in-last-out", "alternate"];
const POLL_CONNECTORS = ["zkbiotime", "suprema", "hikvision", "dahua", "anviz"];
export const MOCK_DEVICE_IP = "10.20.1.99";

export function maskToken(t: string): string {
  if (!t) return "";
  return t.length <= 4 ? "••••" : "••••" + t.slice(-4);
}

/** Public view of a device (server-only fields stripped, token optionally masked). */
export function pubDevice(d: DeviceRec, mask = true): T.Device {
  const { tenantId: _t, secret: _s, attlogStamp: _a, ...pub } = d;
  void _t; void _s; void _a;
  return mask ? { ...pub, webhookToken: maskToken(pub.webhookToken) } : pub;
}

export function validateCommand(kind: string, payload: Record<string, string> = {}): Record<string, string> {
  const errs: Record<string, string> = {};
  if (!COMMAND_KINDS.includes(kind as T.DeviceCommand["kind"])) { errs.kind = "must be one of " + COMMAND_KINDS.join(", "); return errs; }
  const p = payload || {};
  if (kind === "sync_user" || kind === "delete_user") {
    if (!str(p.pin).trim()) errs["payload.pin"] = "required";
    else if (/[\t\r\n]/.test(p.pin)) errs["payload.pin"] = "invalid";
    if (/[\t\r\n]/.test(str(p.name) + str(p.card))) errs["payload.name"] = "invalid characters";
  } else if (kind === "set_time") {
    if (p.unix && !/^[+-]?\d+$/.test(p.unix)) errs["payload.unix"] = "must be unix seconds";
  }
  return errs;
}

export function formatZkCommand(cmd: T.DeviceCommand, nowUnix: number): string {
  const p = cmd.payload;
  const first = (...ks: string[]) => { for (const k of ks) if (p[k]) return p[k]; return ""; };
  let s: string;
  switch (cmd.kind) {
    case "sync_user": s = `DATA UPDATE USERINFO PIN=${p.pin ?? ""}\tName=${p.name ?? ""}\tPrivilege=0\tCard=${p.card ?? ""}`; break;
    case "delete_user": s = "DATA DELETE USERINFO PIN=" + (p.pin ?? ""); break;
    case "reboot": s = "REBOOT"; break;
    case "query_logs": s = `DATA QUERY ATTLOG StartTime=${first("start", "startTime", "StartTime")}\tEndTime=${first("end", "endTime", "EndTime")}`; break;
    case "info": s = "INFO"; break;
    case "set_time": s = "SET OPTION DateTime=" + (first("unix", "time") || String(nowUnix)); break;
    case "clear_logs": s = "CLEAR LOG"; break;
    default: s = String(cmd.kind).toUpperCase();
  }
  return `C:${cmd.id}:${s}`;
}

export function zkOptions(sn: string, stamp: string): string {
  return [`GET OPTION FROM: ${sn}`, `ATTLOGStamp=${stamp || "0"}`, "OPERLOGStamp=9999", "ATTPHOTOStamp=None", "ErrorDelay=30", "Delay=10",
    "TransTimes=00:00;14:05", "TransInterval=1", "TransFlag=TransData AttLog OpLog EnrollUser", "TimeZone=3", "Realtime=1", "Encrypt=None"].join("\n") + "\n";
}

const isoMs = (t: string) => Date.UTC(+t.slice(0, 4), +t.slice(5, 7) - 1, +t.slice(8, 10), +t.slice(11, 13), +t.slice(14, 16), +(t.slice(17, 19) || 0));

function isPrivateHost(baseUrl: string): boolean {
  let host = "";
  try { host = new URL(baseUrl).hostname; } catch { return true; }
  const m = /^(\d+)\.(\d+)\.(\d+)\.(\d+)$/.exec(host);
  if (!m) return host.endsWith(".local") || host.endsWith(".lan");
  const [a, b] = [Number(m[1]), Number(m[2])];
  return a === 10 || (a === 172 && b >= 16 && b <= 31) || (a === 192 && b === 168) || (a === 169 && b === 254);
}

function validHttpUrl(raw: string): boolean {
  try {
    const u = new URL(raw);
    return (u.protocol === "http:" || u.protocol === "https:") && !!u.host && !u.username && !u.password;
  } catch { return false; }
}

/** Connection values for the device wizard (mirrors api/internal/biometric/connection.go). */
export function connectionInfoFor(requestHost: string): T.ConnectionInfo {
  let raw = requestHost.trim();
  let schemeHttps: boolean | null = null;
  const si = raw.indexOf("://");
  if (si >= 0) { schemeHttps = raw.slice(0, si).toLowerCase() === "https"; raw = raw.slice(si + 3); }
  raw = raw.split(/[/?#]/)[0];
  if (raw.includes("@")) raw = raw.slice(raw.lastIndexOf("@") + 1);
  if (!raw) raw = "localhost";
  let hostname = raw, port = 0;
  const v6 = /^\[([^\]]+)\](?::(\d+))?$/.exec(raw);
  if (v6) { hostname = v6[1]; port = Number(v6[2] || 0); }
  else if (/^[^:]+:\d+$/.test(raw)) { const i = raw.lastIndexOf(":"); hostname = raw.slice(0, i); port = Number(raw.slice(i + 1)); }
  if (!(port >= 1 && port <= 65535)) port = 0;
  hostname = hostname.toLowerCase();
  const loop = hostname === "localhost" || hostname === "127.0.0.1" || hostname === "::1" || hostname.endsWith(".localhost");
  const https = schemeHttps ?? !loop;
  const defPort = https ? 443 : 80;
  let hostForUrl = hostname.includes(":") ? `[${hostname}]` : hostname;
  if (port && port !== defPort) hostForUrl += ":" + port;
  return {
    pushHost: hostname, pushPort: port || defPort, https, pushPath: "/iclock/cdata",
    webhookBase: (https ? "https" : "http") + "://" + hostForUrl + "/api/v1/ingest/", agentDownloadUrl: "/downloads/starthr-agent", timezoneOffset: 3,
  };
}

export function biometricOps(c: Ctx) {
  const tenantDevice = (id: string): DeviceRec => {
    const d = c.state.devices.find((x) => x.id === id && x.tenantId === c.tid);
    if (!d) throw E.notFound("device not found");
    return d;
  };
  const serialTaken = (serial: string, exceptId: string) => c.state.devices.some((d) => d.serial === serial && d.id !== exceptId);
  const queueCmd = (deviceId: string, kind: T.DeviceCommand["kind"], payload: Record<string, string> = {}): T.DeviceCommand => {
    const id = c.nextId("commands", 1, () => c.state.commands.reduce((m, x) => Math.max(m, x.id), 0));
    const cmd: T.DeviceCommand = { id, deviceId, kind, payload: { ...payload }, status: "queued", createdAt: c.now, sentAt: null, doneAt: null, result: "" };
    c.state.commands.push(cmd);
    return cmd;
  };

  /** Merge DeviceInput into d and validate (throws 422). Returns the new secret, if any. */
  const applyInput = (d: DeviceRec, inp: Partial<T.DeviceInput>, creating: boolean): string | undefined => {
    const fe: Record<string, string> = {};
    const set = <K extends keyof T.DeviceInput & keyof DeviceRec>(k: K) => {
      const v = inp[k];
      if (v != null) (d as unknown as Record<string, unknown>)[k] = typeof v === "string" ? v.trim() : v;
    };
    for (const k of ["name", "vendor", "protocol", "model", "serial", "branch", "timezone", "directionMode", "baseUrl", "username"] as const) set(k);
    if (inp.pollIntervalSec != null) d.pollIntervalSec = inp.pollIntervalSec;
    if (!d.timezone) d.timezone = "Asia/Riyadh";
    if (!d.directionMode) d.directionMode = "device";
    if (!d.pollIntervalSec && (creating || inp.pollIntervalSec == null)) d.pollIntervalSec = 300;
    if (!d.name) fe.name = "required";
    const v = VENDORS.find((x) => x.id === d.vendor);
    if (!v) fe.vendor = "unknown vendor";
    else if (!v.protocols.includes(d.protocol)) fe.protocol = `must be one of ${v.protocols.join(", ")} for ${d.vendor}`;
    if (!d.serial) fe.serial = "required";
    else if (d.serial.length > 64 || /[ \t\r\n/?&]/.test(d.serial)) fe.serial = "invalid serial";
    if (d.protocol === "poll" && !d.baseUrl) fe.baseUrl = "required for poll devices";
    else if (d.baseUrl && !validHttpUrl(d.baseUrl)) fe.baseUrl = "must be an http(s) URL";
    if (!validTimezone(d.timezone)) fe.timezone = "unknown IANA time zone";
    if (!isInt(d.pollIntervalSec) || d.pollIntervalSec < 60 || d.pollIntervalSec > 86400) fe.pollIntervalSec = "must be between 60 and 86400";
    if (!DIRECTION_MODES.includes(d.directionMode)) fe.directionMode = "must be device, first-in-last-out or alternate";
    if (Object.keys(fe).length) throw E.validation(fe);
    return inp.secret;
  };

  return {
    async biometricOverview(): Promise<T.BiometricOverview> {
      c.principal("biometric");
      const own = c.state.devices.filter((d) => d.tenantId === c.tid);
      const o: T.BiometricOverview = { devices: own.length, online: 0, offline: 0, pending: c.state.devices.filter((d) => d.tenantId === "" && !d.claimed).length, punchesToday: 0, unmatched: 0, lastPunchAt: null };
      for (const d of own) {
        if (d.status === "online") o.online++; else if (d.status === "offline") o.offline++;
        if (d.lastPunchAt && (!o.lastPunchAt || d.lastPunchAt > o.lastPunchAt)) o.lastPunchAt = d.lastPunchAt;
      }
      const tomorrow = addDays(c.today, 1);
      o.punchesToday = c.state.punches.filter((p) => p.time >= c.today && p.time < tomorrow).length;
      o.unmatched = unmatched(c).length;
      return o;
    },

    async bioVendors(): Promise<T.BioVendorInfo[]> {
      c.principal("biometric");
      return VENDORS;
    },

    async connectionInfo(): Promise<T.ConnectionInfo> {
      c.principal("biometric");
      const loc = (globalThis as { location?: { host?: string; protocol?: string } }).location;
      const host = loc && loc.host ? loc.host : "hr.starthr.gulfunionozone.com";
      return connectionInfoFor(host);
    },

    async listDevices(): Promise<T.Device[]> {
      c.principal("biometric");
      const all = c.state.devices.filter((d) => d.tenantId === c.tid || (d.tenantId === "" && !d.claimed));
      all.sort((a, b) => (a.claimed !== b.claimed ? (a.claimed ? 1 : -1) : a.name < b.name ? -1 : a.name > b.name ? 1 : a.id < b.id ? -1 : 1));
      return all.map((d) => pubDevice(d));
    },

    async createDevice(input: T.DeviceInput): Promise<T.Device> {
      const u = c.principal("biometric");
      const d: DeviceRec = {
        id: "", name: "", vendor: "" as T.BioVendor, protocol: "" as T.BioProtocol, model: "", serial: "", branch: "", timezone: "", status: "offline",
        claimed: true, lastSeen: null, lastPunchAt: null, ip: "", firmware: "", userCount: 0, punchCount: 0, directionMode: "" as T.Device["directionMode"],
        pollIntervalSec: 0, baseUrl: "", username: "", hasSecret: false, webhookToken: "", cursor: "", createdAt: "", tenantId: c.tid, secret: "", attlogStamp: "",
      };
      const sec = applyInput(d, input || ({} as T.DeviceInput), true);
      if (serialTaken(d.serial, "")) throw E.conflict("serial already registered");
      d.id = "dev-" + randomHex(4);
      d.webhookToken = randomHex(16);
      d.createdAt = c.now;
      if (sec) { d.secret = sec; d.hasSecret = true; }
      c.state.devices.push(d);
      c.audit(u, "Added device", "Device", d.id, d.vendor + " " + d.serial);
      return pubDevice(d, false);
    },

    async updateDevice(id: string, patch: Partial<T.DeviceInput>): Promise<T.Device> {
      const u = c.principal("biometric");
      const cur = tenantDevice(id);
      const d = structuredClone(cur);
      const sec = applyInput(d, patch || {}, false);
      if (serialTaken(d.serial, d.id)) throw E.conflict("serial already registered");
      if (sec) { d.secret = sec; d.hasSecret = true; }
      Object.assign(cur, d);
      c.audit(u, "Updated device", "Device", d.id, d.name);
      return pubDevice(cur);
    },

    async deleteDevice(id: string): Promise<void> {
      const u = c.principal("biometric");
      const d = tenantDevice(id);
      c.state.devices = c.state.devices.filter((x) => x !== d);
      c.state.commands = c.state.commands.filter((x) => !(x.deviceId === d.id && (x.status === "queued" || x.status === "sent")));
      c.audit(u, "Deleted device", "Device", d.id, d.name);
    },

    async claimDevice(id: string, input: { name: string; branch: string }): Promise<T.Device> {
      const u = c.principal("biometric");
      const d = c.state.devices.find((x) => x.id === id);
      if (!d || (d.tenantId !== "" && d.tenantId !== c.tid)) throw E.notFound("device not found");
      if (d.claimed) throw E.conflict("device already claimed");
      const name = str(input?.name).trim();
      if (!name) throw E.field("name", "required");
      d.tenantId = c.tid;
      d.claimed = true;
      d.status = "online";
      d.name = name;
      d.branch = str(input?.branch).trim();
      if (!d.webhookToken) d.webhookToken = randomHex(16);
      c.audit(u, "Claimed device", "Device", d.id, d.serial);
      return pubDevice(d);
    },

    async testDevice(id: string): Promise<{ ok: boolean; message: string }> {
      c.principal("biometric");
      const d = tenantDevice(id);
      switch (d.protocol) {
        case "push":
          if (!d.lastSeen) return { ok: false, message: "Device has not connected yet" };
          if (isoMs(c.now) - isoMs(d.lastSeen) <= 5 * 60000) return { ok: true, message: `Device is online (last seen ${d.lastSeen})` };
          return { ok: false, message: `No contact for more than 5 minutes (last seen ${d.lastSeen})` };
        case "poll":
          if (!d.baseUrl) return { ok: false, message: "Base URL not configured" };
          if (!POLL_CONNECTORS.includes(d.vendor)) return { ok: false, message: "No connector for vendor " + d.vendor };
          if (isPrivateHost(d.baseUrl)) return { ok: false, message: "Device is on a private network — not reachable from the cloud (use the StartHR Agent)" };
          return { ok: true, message: "Connected and authenticated" };
        default:
          return d.webhookToken ? { ok: true, message: "Token configured — waiting for data" } : { ok: false, message: "No token configured" };
      }
    },

    async syncDevice(id: string): Promise<T.IngestResult> {
      const u = c.principal("biometric");
      const d = tenantDevice(id);
      const res: T.IngestResult = { accepted: 0, duplicates: 0, unmatched: 0, rejected: 0, errors: [] };
      if (d.protocol === "push") {
        const cmd = queueCmd(d.id, "query_logs", { start: c.today + " 00:00:00", end: c.today + " 23:59:59" });
        c.audit(u, "Queued log query", "Device", d.id, String(cmd.id));
      } else if (d.protocol === "poll") {
        // The mock has no network access: connectors return nothing (like the API's demo mode).
        c.audit(u, "Synced device", "Device", d.id, `${res.accepted} accepted`);
      }
      return res;
    },

    async rotateDeviceToken(id: string): Promise<T.Device> {
      const u = c.principal("biometric");
      const d = tenantDevice(id);
      d.webhookToken = randomHex(16);
      c.audit(u, "Rotated device token", "Device", d.id, "");
      return pubDevice(d, false);
    },

    async listPunches(q: T.PunchQuery = {}): Promise<T.Page<T.Punch>> {
      c.principal("biometric");
      const fe: Record<string, string> = {};
      let page = 1, size = 25;
      if (q.page != null) { if (!isInt(q.page) || q.page < 1) fe.page = "must be an integer ≥ 1"; else page = q.page; }
      if (q.pageSize != null) { if (!isInt(q.pageSize) || q.pageSize < 1) fe.pageSize = "must be an integer between 1 and 1000"; else size = Math.min(1000, q.pageSize); }
      const conds: ((t: string) => boolean)[] = [];
      if (q.date) { if (!isISODate(q.date)) fe.date = "valid date required"; else { const a = q.date, b = addDays(q.date, 1); conds.push((t) => t >= a && t < b); } }
      if (q.from) { if (!isISODate(q.from)) fe.from = "valid date required"; else { const a = q.from; conds.push((t) => t >= a); } }
      if (q.to) { if (!isISODate(q.to)) fe.to = "valid date required"; else { const b = addDays(q.to, 1); conds.push((t) => t < b); } }
      if (q.employeeId != null && !isInt(q.employeeId)) fe.employeeId = "must be an integer";
      if (Object.keys(fe).length) throw E.validation(fe);
      const items = c.state.punches.filter((p) => {
        if (!conds.every((f) => f(p.time))) return false;
        if (q.deviceId && p.deviceId !== q.deviceId) return false;
        if (q.unmatched === true) { if (p.employeeId != null) return false; }
        else if (q.employeeId != null) { if (p.employeeId !== q.employeeId) return false; }
        else if (q.unmatched === false && p.employeeId == null) return false;
        return true;
      }).sort((a, b) => (a.time < b.time ? 1 : a.time > b.time ? -1 : a.id < b.id ? 1 : -1));
      return { items: items.slice((page - 1) * size, page * size), total: items.length, page, pageSize: size };
    },

    async importPunches(deviceId: string, csv: string): Promise<T.IngestResult> {
      const u = c.principal("biometric");
      const d = tenantDevice(deviceId);
      if (!str(csv).trim()) throw E.field("body", "CSV content required");
      const res = ingest(c, d, parseCsvPunches(csv), "file");
      c.audit(u, "Imported punches", "Device", d.id, `${res.accepted} accepted, ${res.duplicates} duplicates, ${res.rejected} rejected`);
      return res;
    },

    async unmatchedUsers(): Promise<T.UnmatchedUser[]> {
      c.principal("biometric");
      return unmatched(c);
    },

    async listMappings(): Promise<T.DeviceUserMap[]> {
      c.principal("biometric");
      return c.state.mappings;
    },

    async mapUser(m: T.DeviceUserMap): Promise<T.DeviceUserMap> {
      const u = c.principal("biometric");
      const devId = str(m?.deviceId), user = str(m?.deviceUserId).trim();
      const fe: Record<string, string> = {};
      if (!devId) fe.deviceId = "required";
      if (!user) fe.deviceUserId = "required";
      if (!isInt(m?.employeeId)) fe.employeeId = "required";
      if (Object.keys(fe).length) throw E.validation(fe);
      c.emp(m.employeeId);
      if (devId !== "*") tenantDevice(devId);
      const mp: T.DeviceUserMap = { deviceId: devId, deviceUserId: user, employeeId: m.employeeId };
      const i = c.state.mappings.findIndex((x) => x.deviceId === devId && x.deviceUserId === user);
      if (i >= 0) c.state.mappings[i] = mp; else c.state.mappings.push(mp);
      let n = 0;
      for (const p of c.state.punches) {
        if (p.employeeId == null && p.deviceUserId === user && (devId === "*" || p.deviceId === devId)) { p.employeeId = m.employeeId; n++; }
      }
      c.audit(u, "Mapped device user", "DeviceUserMap", devId + "|" + user, `employee ${m.employeeId}, ${n} punches re-resolved`);
      return mp;
    },

    async unmapUser(deviceId: string, deviceUserId: string): Promise<void> {
      const u = c.principal("biometric");
      const i = c.state.mappings.findIndex((x) => x.deviceId === deviceId && x.deviceUserId === deviceUserId);
      if (i < 0) throw E.notFound("mapping not found");
      c.state.mappings.splice(i, 1);
      c.audit(u, "Unmapped device user", "DeviceUserMap", deviceId + "|" + deviceUserId, "");
    },

    async listCommands(deviceId?: string): Promise<T.DeviceCommand[]> {
      c.principal("biometric");
      return c.state.commands.filter((x) => !deviceId || x.deviceId === deviceId).sort((a, b) => a.id - b.id);
    },

    async queueCommand(deviceId: string, kind: T.DeviceCommand["kind"], payload?: Record<string, string>): Promise<T.DeviceCommand> {
      const u = c.principal("biometric");
      const d = tenantDevice(deviceId);
      const fe = validateCommand(kind, payload);
      if (Object.keys(fe).length) throw E.validation(fe);
      const cmd = queueCmd(d.id, kind, payload);
      c.audit(u, "Queued device command", "Device", d.id, kind);
      return cmd;
    },

    async syncEmployeesToDevice(deviceId: string, employeeIds?: number[]): Promise<T.DeviceCommand[]> {
      const u = c.principal("biometric");
      const d = tenantDevice(deviceId);
      const targets = employeeIds ? employeeIds.map((id) => c.emp(id)) : c.active();
      const out = targets.map((e) => queueCmd(d.id, "sync_user", { pin: String(e.id), name: e.nameEn }));
      c.audit(u, "Pushed users to device", "Device", d.id, `${out.length} users`);
      return out;
    },
  };
}

/* ---------- device-side simulators (no JWT — the device authenticates by SN / token) ---------- */
export function deviceSimulators(c: Ctx) {
  /** ZK ADMS contact: finds the device by SN (auto-registers unknown SNs as pending) and records lastSeen/ip/status. */
  const zkDevice = (serial: string): DeviceRec => {
    const sn = str(serial).trim();
    if (!sn || sn.length > 64 || /[ \t\r\n/?&]/.test(sn)) throw E.badRequest("missing SN");
    let d = c.state.devices.find((x) => x.serial === sn);
    if (!d) {
      d = {
        id: "dev-" + randomHex(4), name: "New device " + sn, vendor: "zkteco", protocol: "push", model: "", serial: sn, branch: "", timezone: "Asia/Riyadh",
        status: "pending", claimed: false, lastSeen: null, lastPunchAt: null, ip: "", firmware: "", userCount: 0, punchCount: 0, directionMode: "device",
        pollIntervalSec: 300, baseUrl: "", username: "", hasSecret: false, webhookToken: "", cursor: "", createdAt: c.now, tenantId: "", secret: "", attlogStamp: "",
      };
      c.state.devices.push(d);
    }
    d.lastSeen = c.now;
    d.ip = MOCK_DEVICE_IP;
    if (d.status !== "pending") d.status = "online";
    return d;
  };
  const claimed = (d: DeviceRec) => d.claimed && d.tenantId !== "";

  return {
    /** GET /iclock/cdata?SN=&options=all → options text. */
    async simulateDeviceHandshake(serial: string): Promise<string> {
      const d = zkDevice(serial);
      return zkOptions(d.serial, d.attlogStamp);
    },

    /** POST /iclock/cdata?SN=&table=ATTLOG → "OK: <accepted>". Unclaimed devices store nothing ("OK: 0"). */
    async simulateDevicePush(serial: string, attlogBody: string, stamp?: string): Promise<{ reply: string; result: T.IngestResult }> {
      const d = zkDevice(serial);
      const empty: T.IngestResult = { accepted: 0, duplicates: 0, unmatched: 0, rejected: 0, errors: [] };
      if (!claimed(d)) return { reply: "OK: 0", result: empty };
      const result = ingest(c, d, parseZkAttlog(attlogBody), "push");
      if (stamp) d.attlogStamp = stamp;
      return { reply: "OK: " + result.accepted, result };
    },

    /** POST /iclock/cdata?SN=&table=OPERLOG with "USER PIN=…" lines → upserts device user names. */
    async simulateDeviceUsers(serial: string, operlogBody: string): Promise<string> {
      const d = zkDevice(serial);
      if (claimed(d)) for (const x of parseZkUsers(operlogBody)) c.state.deviceUsers[d.id + "|" + x.pin] = x.name;
      return "OK";
    },

    /** GET /iclock/getrequest?SN= → next queued command "C:<id>:<CMD>" (status → sent) or "OK". */
    async simulateGetRequest(serial: string): Promise<string> {
      const d = zkDevice(serial);
      if (!claimed(d)) return "OK";
      const cmd = c.state.commands.filter((x) => x.deviceId === d.id && x.status === "queued").sort((a, b) => a.id - b.id)[0];
      if (!cmd) return "OK";
      cmd.status = "sent";
      cmd.sentAt = c.now;
      return formatZkCommand(cmd, Math.floor((Date.parse(c.now + "+03:00")) / 1000)) + "\n";
    },

    /** POST /iclock/devicecmd?SN= body "ID=<id>&Return=<code>&CMD=<cmd>" lines → "OK". */
    async simulateDeviceCmdResult(serial: string, body: string): Promise<string> {
      const d = zkDevice(serial);
      if (!claimed(d)) return "OK";
      for (const raw of body.replace(/\r/g, "").split("\n")) {
        const line = raw.trim();
        if (!line) continue;
        const vals = new URLSearchParams(line);
        const id = Number(vals.get("ID"));
        const cmd = c.state.commands.find((x) => x.id === id && x.deviceId === d.id);
        if (!Number.isInteger(id) || !cmd) continue;
        cmd.doneAt = c.now;
        cmd.status = vals.get("Return") === "0" ? "done" : "failed";
        cmd.result = line.slice(0, 500);
      }
      return "OK";
    },

    /** POST /api/v1/ingest/{deviceId}?token= — JSON value/text or CSV text. 404 unknown device, 401 bad token. */
    async simulateWebhook(deviceId: string, token: string, payload: unknown): Promise<T.IngestResult> {
      const d = c.state.devices.find((x) => x.id === deviceId);
      if (!d) throw E.notFound("device not found");
      if (!d.webhookToken || !token || token !== d.webhookToken) throw E.unauthorized("invalid device token");
      if (payload == null || (typeof payload === "string" && !payload.trim())) throw E.badRequest("empty body");
      let res;
      try { res = parseWebhook(payload, tzOffsetMinutes(d.timezone, c.now)); } catch { throw E.badRequest("invalid JSON"); }
      return ingest(c, d, res, d.protocol === "agent" ? "agent" : "webhook");
    },
  };
}
